<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useFunctionGetter,
  useStoreGetters,
} from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { fetchWooCommerceOrders } from '../../../api/woocommerce';
import WooCommerceOrderItem from './WooCommerceOrderItem.vue';
import {
  filterWooCommerceRecordsByConversationMode,
  groupWooCommerceRecordsByStore,
} from '../../../helpers/stores';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  conversationMode: {
    type: String,
    default: '',
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;
const contact = useFunctionGetter('contacts/getContact', props.contactId);
const integration = useFunctionGetter(
  'integrations/getIntegration',
  'woocommerce'
);
const { t } = useI18n();

const hasSearchableInfo = computed(
  () => !!contact.value?.email || !!contact.value?.phone_number
);

const orders = ref([]);
const loading = ref(true);
const error = ref('');
const visibleOrders = computed(() =>
  filterWooCommerceRecordsByConversationMode(
    orders.value,
    integration.value?.hooks || [],
    props.conversationMode
  )
);
const groupedOrders = computed(() =>
  groupWooCommerceRecordsByStore(visibleOrders.value)
);
const translatedError = computed(() =>
  error.value ? t('CONVERSATION_SIDEBAR.WOOCOMMERCE.ERROR') : ''
);

const fetchOrders = async () => {
  try {
    loading.value = true;
    orders.value = [];
    error.value = '';

    const response = await fetchWooCommerceOrders(
      accountId.value,
      props.contactId
    );
    orders.value = response.data.orders;
  } catch {
    error.value = 'CONVERSATION_SIDEBAR.WOOCOMMERCE.ERROR';
  } finally {
    loading.value = false;
  }
};

watch(
  () => [props.contactId, hasSearchableInfo.value],
  () => {
    if (hasSearchableInfo.value) {
      fetchOrders();
    } else {
      loading.value = false;
      orders.value = [];
      error.value = '';
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <div v-if="!hasSearchableInfo" class="text-center text-n-slate-12">
      {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.NO_ORDERS') }}
    </div>
    <div v-else-if="loading" class="flex justify-center items-center p-4">
      <Spinner size="32" class="text-n-brand" />
    </div>
    <div v-else-if="error" class="text-center text-n-ruby-12">
      {{ translatedError }}
    </div>
    <div v-else-if="!visibleOrders.length" class="text-center text-n-slate-12">
      {{ $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.NO_ORDERS') }}
    </div>
    <div v-else class="space-y-3">
      <div
        v-for="group in groupedOrders"
        :key="group.id"
        class="rounded-md border border-n-weak bg-n-alpha-2 p-3"
      >
        <p class="mb-2 text-xs font-semibold text-n-slate-11">
          {{
            $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.STORE_GROUP', {
              store: group.label,
              count: group.records.length,
            })
          }}
        </p>
        <WooCommerceOrderItem
          v-for="order in group.records"
          :key="`${group.id}-${order.id}`"
          :order="order"
        />
      </div>
    </div>
  </div>
</template>
